/**
 * When the fixed HomeCam cover shipped. A signature made before this was made
 * on the old cover (selectable company logo), so that report keeps it (V67).
 * Signatures from this point on were made on the HomeCam cover and keep it.
 * Set to the production deploy time of ticket 13213612910.
 */
export const COVER_HOMECAM_SINCE = Date.UTC(2026, 9, 8, 0, 55) // 2026-10-07 20:55 VET = web release of signing v2

/**
 * True if any of the given `[signature, signatureDate]` pairs was signed
 * before {@link COVER_HOMECAM_SINCE}. A signature with no usable date counts
 * as old: freezing a cover by mistake is safer than changing a signed one.
 */
export const signedBeforeHomeCamCover = (
  pairs: readonly (readonly [unknown, unknown])[],
): boolean =>
  pairs.some(
    ([sign, date]) =>
      !!sign && !(typeof date === 'number' && date >= COVER_HOMECAM_SINCE),
  )

/**
 * Ticket 13213612910 (Luis + Jaron, 2026-10-05): the report cover always shows
 * our HomeCam logo, not the selectable contract company — that selector stays
 * on the contract pages.
 *
 * Only once a company is picked: with none, the cover selector is the only
 * place a rep can choose one (no contract page renders without a company).
 * Never on a customer that signed anything before the HomeCam cover shipped,
 * so those reports keep the cover they were signed with (V67). Falls back to
 * the old behavior when the HC record has no logo.
 */
export const coverHomeCamLogo = ({
  hcLogo,
  shortName,
  signedBefore,
}: {
  hcLogo: string | undefined
  shortName: string
  signedBefore: boolean
}): string | undefined =>
  shortName && !signedBefore && hcLogo ? hcLogo : undefined

/**
 * Jaron's Nov 4 video (Thomas Saillant): he picked Olson on the contract and
 * the cover stayed on HomeCam until he picked it again on the cover selector.
 * With nothing picked for the cover and no signature at all, these companies
 * print their own logo on the cover. FE, HC and M+S stay on HomeCam (ticket
 * 13213612910), and so does anyone with a signature (V67).
 */
export const COVER_FOLLOWS_CONTRACT: ReadonlySet<string> = new Set([
  'ESR',
  'HNNV',
  'OLSON',
  'ROOFNOW',
])

export const coverFollowsContract = ({
  coverCompany,
  shortName,
  signed,
}: {
  coverCompany: string
  shortName: string
  signed: boolean
}): boolean => !coverCompany && !signed && COVER_FOLLOWS_CONTRACT.has(shortName)

const ownLogo = (
  logos: Readonly<Record<string, string | undefined>>,
  key: string,
): string | undefined => {
  const logo = Object.prototype.hasOwnProperty.call(logos, key)
    ? logos[key]
    : undefined
  return logo || undefined
}

/**
 * Monday 13312360011: the rep can now pick the cover company on its own
 * (`coverCompany`, independent of the contract company).
 *
 * `logos` maps a short name to the logo to print: contract companies (HNNV
 * already swapped for its gray one, V76) plus the cover-only brands. With
 * nothing picked the result is `coverHomeCamLogo`, except while
 * `coverFollowsContract` holds; a signed customer always keeps the cover it
 * has today (V67). A pick whose logo is unknown yields undefined: the caller
 * shows the company name.
 */
export const resolveCoverLogo = ({
  coverCompany,
  hcLogo,
  logos,
  shortName,
  signed,
  signedBefore,
}: {
  coverCompany: string
  hcLogo: string | undefined
  logos: Readonly<Record<string, string | undefined>>
  shortName: string
  /** Any signature at all, old or new: see `coverHasSignature`. */
  signed: boolean
  signedBefore: boolean
}): string | undefined => {
  if (coverCompany) return ownLogo(logos, coverCompany)
  const following = coverFollowsContract({ coverCompany, shortName, signed })
    ? ownLogo(logos, shortName)
    : undefined
  return following || coverHomeCamLogo({ hcLogo, shortName, signedBefore })
}
